import React, { useState } from 'react';
import {
  Badge,
  Box,
  Collapse,
  Flex,
  Grid,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
  useColorModeValue,
  Button,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  MdExpandLess,
  MdExpandMore,
  MdScale,
  MdRoute,
} from 'react-icons/md';
import { FaRoute } from 'react-icons/fa';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import {
  UnifiedEmptyState,
  UnifiedLoadingState,
  UnifiedResultsSection,
  UnifiedSurfaceCard,
  useUnifiedButtonStyles,
  useUnifiedInteractiveCardStyles,
  useUnifiedSurfaceStyles,
} from 'components/layout/UnifiedPrimitives';
import ListingItemsSection from 'domains/listing/components/ListingItemsSection';
import { useTranslation } from 'i18n/TranslationProvider';
import { translateRaw } from 'i18n/globalTranslator';
import { formatDateWithOptions } from 'utilities/dateTimeUtils';
import {
  mapTemplateTitleToTranslation,
  CATEGORIES,
} from 'domains/shared/utils/categoryUtils';
import { useGetPartnerCompletedListings } from '../hooks/useGetPartnerCompletedListings';

// --- Local Helpers (Copied from SearchListings.jsx for 1:1 display) ---

function formatDateRange(from, to) {
  if (!from && !to) return translateRaw('Flexible dates');
  const fmt = (date) =>
    date
      ? formatDateWithOptions(date, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '?';
  if (from && to && from !== to) return `${fmt(from)} – ${fmt(to)}`;
  return fmt(from || to);
}

function formatCategoryLabel(value) {
  return String(value || 'other').replace(/_/g, ' ');
}

function getItemDetails(listing) {
  const items = Array.isArray(listing.items)
    ? listing.items.filter(Boolean)
    : [];
  if (!items.length)
    return {
      title: translateRaw('No item details'),
      facts: [],
      dimensions: null,
    };

  const first = items[0] || {};
  const dimensionSource =
    items.find(
      (entry) =>
        entry?.weight || entry?.length || entry?.width || entry?.height,
    ) || first;

  const title =
    [first.year, first.make, first.model].filter(Boolean).join(' ').trim() ||
    first.itemDescription ||
    first.petType ||
    first.category ||
    translateRaw('Shipment details');

  const facts = [];
  if (items.length)
    facts.push(translateRaw('{{count}} item(s)', { count: items.length }));
  if (first.quantity) facts.push(`${translateRaw('Qty')} ${first.quantity}`);
  if (first.weight) facts.push(`${first.weight} kg`);
  if ([first.length, first.width, first.height].some(Boolean)) {
    facts.push(
      [first.length, first.width, first.height].filter(Boolean).join(' × ') +
        ' cm',
    );
  }

  const dimensions = {
    weight: dimensionSource.weight,
    length: dimensionSource.length,
    width: dimensionSource.width,
    height: dimensionSource.height,
    itemCount: items.length,
  };

  return { title, facts: facts.slice(0, 4), dimensions };
}

function formatDistance(distanceKm) {
  if (!distanceKm) return null;
  const km = Number(distanceKm);
  if (isNaN(km)) return null;
  if (km < 1) return `${(km * 1000).toFixed(0)} ${translateRaw('m')}`;
  if (km < 10) return `${km.toFixed(1)} ${translateRaw('booking.km')}`;
  return `${Math.round(km)} ${translateRaw('booking.km')}`;
}

function formatMoney(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? `EUR ${numeric.toFixed(2)}`
    : null;
}

function formatListingStatus(value) {
  const normalized = String(value || 'open')
    .replace(/_/g, ' ')
    .trim();
  return normalized
    ? translateRaw(normalized.toUpperCase())
    : translateRaw('OPEN');
}

function getMapThumbnailUrl(listing) {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const origin = listing.fromCoords || listing.publicFromCoords;
  const destination = listing.toCoords || listing.publicToCoords;
  if (!apiKey || (!origin && !destination)) return null;

  const parts = ['size=240x180', 'scale=2', 'maptype=roadmap', `key=${apiKey}`];
  if (origin)
    parts.push(`markers=color:0x4285F4|label:A|${origin.lat},${origin.lng}`);
  if (destination)
    parts.push(
      `markers=color:0xEA4335|label:B|${destination.lat},${destination.lng}`,
    );

  if (origin && destination) {
    const latDiff = Math.abs(origin.lat - destination.lat);
    const lngDiff = Math.abs(origin.lng - destination.lng);
    const MIN_SPAN = 0.14;
    const effectiveLat = Math.max(latDiff, MIN_SPAN);
    const effectiveLng = Math.max(lngDiff, MIN_SPAN);
    const PAD = 0.45;
    const minLat = Math.min(origin.lat, destination.lat) - effectiveLat * PAD;
    const maxLat = Math.max(origin.lat, destination.lat) + effectiveLat * PAD;
    const minLng = Math.min(origin.lng, destination.lng) - effectiveLng * PAD;
    const maxLng = Math.max(origin.lng, destination.lng) + effectiveLng * PAD;
    parts.push(`visible=${minLat},${minLng}|${maxLat},${maxLng}`);
  } else {
    const point = origin || destination;
    parts.push(`center=${point.lat},${point.lng}`);
    parts.push('zoom=9');
  }

  return `https://maps.googleapis.com/maps/api/staticmap?${parts.join('&')}`;
}

// --- ResultCard (Copied from SearchListings.jsx for 1:1 display) ---

function ResultCard({ listing }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subtleBg, subtleBorder, mutedText } = useUnifiedSurfaceStyles();
  const { primaryButtonProps, secondaryButtonProps } = useUnifiedButtonStyles();
  const { transition, hoverProps } = useUnifiedInteractiveCardStyles();
  const cardBg = useColorModeValue(
    'rgba(255,255,255,0.92)',
    'rgba(15, 23, 42, 0.9)',
  );
  const titleColor = useColorModeValue('navy.800', 'white');
  const accentColor = useColorModeValue('brand.500', 'brand.200');
  const categoryBadgeBg = useColorModeValue('#6B46F6', '#8B5CF6');
  const timelineLineColor = useColorModeValue('#DDD6FE', '#6D28D9');
  const neutralChipBg = useColorModeValue('#F8FAFC', 'whiteAlpha.120');
  const budgetBg = useColorModeValue('#EEF4FF', 'rgba(49, 130, 206, 0.16)');
  const budgetBadgeBg = useColorModeValue(
    '#E8DDFF',
    'rgba(139, 92, 246, 0.26)',
  );
  const statusBadgeBg = useColorModeValue(
    '#E8EEFF',
    'rgba(72, 187, 255, 0.18)',
  );
  const shadow = useColorModeValue(
    '0 22px 50px rgba(15, 23, 42, 0.08)',
    '0 18px 40px rgba(2, 6, 23, 0.35)',
  );

  const [isExpanded, setIsExpanded] = useState(false);

  const listingId = listing._id || listing.id;
  const category = CATEGORIES.find((option) => option.id === listing.category);
  const quoteCount = listing.quoteCount ?? listing.quotesCount ?? 0;
  const clientPriceText = formatMoney(listing.clientPriceGross);
  const postedDate = listing.createdAt
    ? formatDateWithOptions(listing.createdAt, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';
  const fromCity = listing.fromCity || t('Not set');
  const fromCountry = listing.fromCountry || '';
  const toCity = listing.toCity || t('Not set');
  const toCountry = listing.toCountry || '';

  const fromLabel =
    [fromCity, listing.fromPostcode].filter(Boolean).join(', ') ||
    t('Pick-up pending');
  const toLabel =
    [toCity, listing.toPostcode].filter(Boolean).join(', ') ||
    t('Delivery pending');

  const fromFullLabel =
    [fromCity, fromCountry].filter(Boolean).join(', ') || fromLabel;
  const toFullLabel = [toCity, toCountry].filter(Boolean).join(', ') || toLabel;
  const pickupLabel = formatDateRange(
    listing.pickupDateFrom,
    listing.pickupDateTo,
  );
  const deliveryLabel = formatDateRange(
    listing.deliveryDateFrom,
    listing.deliveryDateTo,
  );
  const clientTypeLabel =
    listing.clientType === 'company' ? t('Company') : t('Private Person');
  const item = getItemDetails(listing);
  const distanceText = formatDistance(
    listing.distanceKm || listing.routeDistanceKm,
  );
  const mapUrl = getMapThumbnailUrl(listing);
  const statusLabel = formatListingStatus(listing.status);
  const weightText = item.dimensions?.weight
    ? `${item.dimensions.weight} ${t('booking.kg')}`
    : null;
  const itemCountText = t('{{count}} item(s)', {
    count: item.dimensions?.itemCount || 0,
  });

  return (
    <UnifiedSurfaceCard
      bg={cardBg}
      p={{ base: 4, md: 5, xl: 6 }}
      borderRadius={{ base: '26px', xl: '34px' }}
      position="relative"
      transition={transition}
      boxShadow={shadow}
      border="1px solid"
      borderColor={subtleBorder}
      _hover={hoverProps}
    >
      <HStack
        position="absolute"
        top={{ base: 4, md: 5, xl: 6 }}
        right={{ base: 4, md: 5, xl: 6 }}
        align="center"
        spacing={3}
        zIndex={1}
        flexWrap="wrap"
        justify="flex-end"
      >
        {listing.humanId && (
          <Text
            fontSize="10px"
            color={mutedText}
            fontWeight="800"
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            #{listing.humanId}
          </Text>
        )}
        <Text
          fontSize="10px"
          color={mutedText}
          fontWeight="800"
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          {t('Posted')} {postedDate}
        </Text>
        <Badge
          bg={categoryBadgeBg}
          color="white"
          borderRadius="full"
          px={3.5}
          py={1}
          fontSize="xs"
          fontWeight="700"
          textTransform="none"
        >
          {t(category?.label || formatCategoryLabel(listing.category))}
        </Badge>
        <Badge
          bg={statusBadgeBg}
          color={accentColor}
          borderRadius="full"
          px={3.5}
          py={1}
          fontSize="xs"
          fontWeight="800"
          textTransform="uppercase"
          letterSpacing="0.04em"
        >
          {t(statusLabel.toLowerCase())}
        </Badge>
      </HStack>
      <Grid
        alignItems="center"
        templateColumns={{
          base: '1fr',
          xl: '220px minmax(260px, 320px) minmax(0, 1fr) 220px',
        }}
      >
        <Box
          w={{ base: '100%', xl: '220px' }}
          h={{ base: '160px', xl: '170px' }}
          borderRadius="22px"
          overflow="hidden"
          bg={subtleBg}
          border="1px solid"
          borderColor={subtleBorder}
          flexShrink={0}
          position="relative"
          boxShadow="0 14px 28px rgba(15, 23, 42, 0.08)"
        >
          {mapUrl ? (
            <Image
              src={mapUrl}
              alt={t('Route map')}
              w="100%"
              h="100%"
              objectFit="cover"
              fallback={
                <Flex w="100%" h="100%" align="center" justify="center">
                  <Icon
                    as={FaRoute}
                    boxSize={8}
                    color={accentColor}
                    opacity={0.5}
                  />
                </Flex>
              }
            />
          ) : (
            <Flex
              w="100%"
              h="100%"
              align="center"
              justify="center"
              direction="column"
              gap={2}
              px={4}
            >
              <Icon
                as={FaRoute}
                boxSize={8}
                color={accentColor}
                opacity={0.5}
              />
              <Text
                fontSize="sm"
                color={mutedText}
                textAlign="center"
                fontWeight="700"
              >
                {fromFullLabel}
              </Text>
              <Text
                fontSize="xs"
                color={mutedText}
                textAlign="center"
                fontWeight="600"
              >
                {toFullLabel}
              </Text>
            </Flex>
          )}
        </Box>

        <HStack
          align="stretch"
          spacing={{ base: 3, md: 4 }}
          minW={0}
          flex={1}
          pl={5}
        >
          <VStack spacing={0} minW={{ base: '44px', md: '54px' }} pt={1}>
            <Flex
              w={{ base: '38px', md: '44px' }}
              h={{ base: '38px', md: '44px' }}
              align="center"
              justify="center"
              borderRadius="full"
              bg="#4285F4"
              color="white"
              fontWeight="800"
              fontSize={{ base: 'md', md: 'lg' }}
              boxShadow="0 8px 18px rgba(66, 133, 244, 0.25)"
            >
              A
            </Flex>
            <Box
              w="2px"
              flex="1"
              minH={{ base: '52px', md: '60px' }}
              bg={timelineLineColor}
              borderRadius="full"
              my={2}
              mx="auto"
            />
            <Flex
              w={{ base: '36px', md: '42px' }}
              h={{ base: '36px', md: '42px' }}
              align="center"
              justify="center"
              borderRadius="full"
              bg="#EA4335"
              color="white"
              fontWeight="800"
              fontSize={{ base: 'md', md: 'lg' }}
              boxShadow="0 14px 24px rgba(234, 67, 53, 0.26)"
            >
              B
            </Flex>
          </VStack>

          <VStack
            align="stretch"
            spacing={{ base: 6, md: 7 }}
            minW={0}
            justify="center"
          >
            <VStack align="start" spacing={0}>
              <Text
                fontSize="10px"
                fontWeight="800"
                color={mutedText}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {t('Pick-up')}
              </Text>
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="800"
                color={titleColor}
                lineHeight="1.3"
                noOfLines={1}
              >
                {fromLabel}
              </Text>
              <Text fontSize="xs" color={mutedText} fontWeight="600">
                {pickupLabel}
              </Text>
            </VStack>

            <VStack align="start" spacing={0}>
              <Text
                fontSize="10px"
                fontWeight="800"
                color={mutedText}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {t('Delivery')}
              </Text>
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="800"
                color={titleColor}
                lineHeight="1.3"
                noOfLines={1}
              >
                {toLabel}
              </Text>
              <Text
                fontSize="xs"
                color={mutedText}
                fontWeight="600"
                noOfLines={1}
              >
                {deliveryLabel}
              </Text>
            </VStack>
          </VStack>
        </HStack>

        <VStack
          align="stretch"
          spacing={{ base: 4, md: 5 }}
          minW={0}
          justify="center"
          flex={1}
        >
          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            fontWeight="700"
            color={titleColor}
            lineHeight="1.25"
            noOfLines={2}
            textTransform="capitalize"
          >
            {mapTemplateTitleToTranslation(listing.title || item.title, t)}
          </Text>

          <HStack spacing={3} flexWrap="wrap">
            <Box
              px={3.5}
              py={2}
              borderRadius="18px"
              bg={neutralChipBg}
              border="1px solid"
              borderColor={subtleBorder}
            >
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="700"
                color={titleColor}
              >
                {clientTypeLabel}
              </Text>
            </Box>
            <Box
              px={3.5}
              py={2}
              borderRadius="18px"
              bg={neutralChipBg}
              border="1px solid"
              borderColor={subtleBorder}
            >
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="700"
                color={titleColor}
              >
                {itemCountText}
              </Text>
            </Box>
            {listing.allowsQuotes === false ? (
              <Box
                px={3.5}
                py={2}
                borderRadius="18px"
                bg={neutralChipBg}
                border="1px solid"
                borderColor={subtleBorder}
              >
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="700"
                  color={titleColor}
                >
                  {t('Fixed price')}
                </Text>
              </Box>
            ) : null}
          </HStack>

          <HStack spacing={{ base: 3, md: 4 }} flexWrap="wrap" align="center">
            {weightText ? (
              <HStack spacing={1.5}>
                <Icon as={MdScale} boxSize={3.5} color={accentColor} />
                <Text fontSize="sm" color={titleColor} fontWeight="700">
                  {weightText}
                </Text>
              </HStack>
            ) : null}
            {distanceText ? (
              <HStack spacing={1.5}>
                <Icon as={MdRoute} boxSize={3.5} color={accentColor} />
                <Text fontSize="sm" color={titleColor} fontWeight="700">
                  {distanceText}
                </Text>
              </HStack>
            ) : null}
          </HStack>

         
        </VStack>

        <VStack
          align={{ base: 'stretch', lg: 'flex-end' }}
          justify="center"
          spacing={3}
          minW={{ lg: '212px' }}
          pt={{ lg: 8 }}
        >
          <Button
            {...primaryButtonProps}
            fontSize="sm"
            w="100%"
            onClick={() => navigate(`/listings/${listingId}`)}
          >
            {t('View details')}
          </Button>

          <Button
            {...secondaryButtonProps}
            size="sm"
            w="100%"
            rightIcon={
              <Icon
                as={isExpanded ? MdExpandLess : MdExpandMore}
                boxSize={4}
              />
            }
            onClick={() => setIsExpanded((v) => !v)}
          >
            {t('quick view')}
          </Button>

          {listing.allowsQuotes !== false && quoteCount > 0 ? (
            <Badge
              alignSelf={{ base: 'flex-start', lg: 'center' }}
              bg={neutralChipBg}
              color={titleColor}
              borderRadius="full"
              px={3}
              py={1}
              fontSize="10px"
              fontWeight="700"
              textTransform="none"
            >
              {t('{{count}} quote(s)', { count: quoteCount })}
            </Badge>
          ) : null}
           {clientPriceText ? (
            <Flex
              px={4}
              py={3.5}
              borderRadius="22px"
              bg={budgetBg}
              border="1px solid"
              borderColor={subtleBorder}
              align="center"
              justify="space-between"
              gap={4}
              maxW={{ base: '100%', xl: '420px' }}
            >
              <Box>
                <Text
                  fontSize="10px"
                  fontWeight="800"
                  color={mutedText}
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  {t('Client Price')}
                </Text>
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight="900"
                  color={titleColor}
                  lineHeight="1.05"
                >
                  {clientPriceText}
                </Text>
              </Box>
              <Badge
                bg={budgetBadgeBg}
                color="#3B1F9B"
                borderRadius="full"
                px={3}
                py={1}
                fontSize="10px"
                fontWeight="800"
                textTransform="uppercase"
                letterSpacing="0.04em"
              >
                {listing.allowsQuotes === false
                  ? t('Fixed price')
                  : t('Target budget')}
              </Badge>
            </Flex>
          ) : null}
        </VStack>
      </Grid>

      <Collapse in={isExpanded} animateOpacity>
        <Box mt={3}>
          <ListingItemsSection
            items={listing.items}
            category={listing.category}
          />
        </Box>
      </Collapse>
    </UnifiedSurfaceCard>
  );
}

// --- Page Component ---

export const PartnerCompletedListings = () => {
  const { t } = useTranslation();
  const { listings, loading, error } = useGetPartnerCompletedListings();

  if (error) {
    return (
      <PageShell>
        <UnifiedSurfaceCard p={6}>
          <Text color="red.500">{error}</Text>
        </UnifiedSurfaceCard>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          title={t('Completed Deliveries')}
          description={t(
            'History of your successfully delivered transport requests.',
          )}
        />

        <UnifiedResultsSection
          title={
            loading
              ? t('Loading deliveries...')
              : t('{{count}} completed deliveries', { count: listings.length })
          }
        >
          {loading ? (
            <UnifiedLoadingState
              label={t('Loading completed deliveries...')}
              minH="40vh"
            />
          ) : (
            <VStack spacing={4} align="stretch">
              {listings.length === 0 ? (
                <UnifiedEmptyState
                  title={t('No completed listings')}
                  description={t('You have no completed deliveries yet.')}
                />
              ) : (
                listings.map((listing, index) => (
                  <ResultCard
                    key={listing._id || listing.id || index}
                    listing={listing}
                  />
                ))
              )}
            </VStack>
          )}
        </UnifiedResultsSection>
      </VStack>
    </PageShell>
  );
};

export default PartnerCompletedListings;
